/**
 * Создание задачи и серии.
 *
 * На первом экране ровно четыре поля (время, тип, название, повтор) —
 * из плана: «создание задачи ≤ 4 полей». Место и напоминание спрятаны
 * под «ещё», потому что нужны не всегда.
 */

import { useState } from 'react'

import {
  KIND_OPTIONS,
  WEEKDAY_LABELS,
  useCreateSeries,
  useCreateTask,
  type TaskKind,
} from '@/shared/api/schedule'
import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Sheet } from '@/shared/ui/Sheet'
import { Field, TextInput } from '@/shared/ui/fields'
import { taskIcon } from '@/shared/ui/icon-map'
import { ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

export function TaskSheet({
  open,
  familyId,
  date,
  onClose,
}: {
  open: boolean
  familyId: number
  date: string
  onClose: () => void
}) {
  const [time, setTime] = useState('17:00')
  const [kind, setKind] = useState<TaskKind>('activity')
  const [title, setTitle] = useState('')
  const [place, setPlace] = useState('')
  const [repeat, setRepeat] = useState(false)
  const [weekdays, setWeekdays] = useState<number[]>([])
  const [more, setMore] = useState(false)
  const [reminder, setReminder] = useState(30)

  const reset = () => {
    setTitle('')
    setPlace('')
    setRepeat(false)
    setWeekdays([])
    setMore(false)
  }

  const done = (message: string) => {
    haptic('success')
    toast.success(message)
    reset()
    onClose()
  }

  const createTask = useCreateTask(familyId, { onSuccess: () => done('Задача добавлена') })
  const createSeries = useCreateSeries(familyId, {
    onSuccess: () => done('Повтор создан на две недели вперёд'),
  })

  const error = createTask.error ?? createSeries.error
  const pending = createTask.isPending || createSeries.isPending
  const canSubmit = title.trim().length > 0 && (!repeat || weekdays.length > 0) && !pending

  const submit = () => {
    if (repeat) {
      createSeries.mutate({
        weekdays,
        time_start: time,
        kind,
        title: title.trim(),
        place: place.trim() || undefined,
        reminder_min: reminder,
      })
    } else {
      createTask.mutate({
        date,
        time_start: time,
        kind,
        title: title.trim(),
        place: place.trim() || undefined,
        reminder_min: reminder,
      })
    }
  }

  return (
    <Sheet
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title="Новая задача"
      footer={
        <Button block size="lg" loading={pending} disabled={!canSubmit} onClick={submit}>
          {repeat ? 'Создать повтор' : 'Добавить'}
        </Button>
      }
    >
      {error && <ErrorBanner message={error.message} />}

      <div className="flex gap-3">
        <Field label="Время">
          <TextInput
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value)}
            className="w-32"
          />
        </Field>
      </div>

      <div className="mt-4">
        <span className="text-text-muted mb-1.5 block px-1 text-caption font-medium">Что</span>
        <div className="grid grid-cols-5 gap-2">
          {KIND_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                haptic('select')
                setKind(option.value)
              }}
              className={
                'press flex flex-col items-center gap-1 rounded-[var(--radius-tile)] py-3 ' +
                (kind === option.value
                  ? 'bg-accent-soft text-accent'
                  : 'bg-surface-muted text-text-muted')
              }
            >
              <Icon name={taskIcon(option.value)} size={18} />
              <span className="text-[11px] leading-tight">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Field label="Название">
          <TextInput
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Английский"
            maxLength={64}
            autoFocus
          />
        </Field>
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => {
            haptic('select')
            setRepeat((value) => !value)
          }}
          className={
            'press tap-target flex w-full items-center gap-2 rounded-[var(--radius-button)] px-4 text-left text-body ' +
            (repeat ? 'bg-accent-soft text-accent' : 'bg-surface-muted text-text-muted')
          }
        >
          {/* Значок отдельным элементом: внутри строки скринридер прочёл бы его как часть подписи. */}
          <Icon name="task-repeat" size={20} />
          {repeat ? 'Повторять по дням недели' : 'Сделать повторяющейся'}
        </button>

        {repeat && (
          <div className="mt-2 grid grid-cols-7 gap-1">
            {WEEKDAY_LABELS.map((label, index) => {
              const day = index + 1
              const active = weekdays.includes(day)
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    haptic('select')
                    setWeekdays((current) =>
                      active ? current.filter((d) => d !== day) : [...current, day],
                    )
                  }}
                  className={
                    'press h-11 rounded-[var(--radius-thumb)] text-caption font-medium ' +
                    (active ? 'bg-accent text-on-accent' : 'bg-surface-muted text-text-muted')
                  }
                >
                  {label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {more ? (
        <div className="mt-4 space-y-4">
          <Field label="Место">
            <TextInput
              value={place}
              onChange={(event) => setPlace(event.target.value)}
              placeholder="ул. Мира 4"
              maxLength={64}
            />
          </Field>
          <Field label="Напомнить за" hint="Напоминание придёт няне в Telegram.">
            <div className="flex gap-2">
              {[0, 15, 30, 60].map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => setReminder(minutes)}
                  className={
                    'press h-11 flex-1 rounded-[var(--radius-button)] text-note ' +
                    (reminder === minutes
                      ? 'bg-accent-soft text-accent'
                      : 'bg-surface-muted text-text-muted')
                  }
                >
                  {minutes === 0 ? 'не надо' : `${minutes} мин`}
                </button>
              ))}
            </div>
          </Field>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setMore(true)}
          className="text-text-muted tap-target mt-3 px-1 text-note"
        >
          Ещё: место и напоминание
        </button>
      )}
    </Sheet>
  )
}
